/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { formatDate } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { NumberInputFormik, TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { ServicingRate } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { AddPatientRelationshipDialog } from 'pages/routeManagement/dialogs/AddPatientRelationshipDialog';
import { DeletePatientRelationshipDialog } from 'pages/routeManagement/dialogs/DeletePatientRelationshipDialog';
import { PayerOption } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, post, put, useGet } from 'services/Http';
import {
    ClaimsCheckMode,
    ContinuityOfCareRoute,
    getRoutes,
    NO_THERAPEUTIC_CATEGORY_DISPLAY_TEXT,
    patientRelationshipRoutesUrl,
    PatientRelationshipType,
    PatientRelationshipTypeDisplayNameMap,
    PatientRelationshipTypeDisplayNameMapPluralized,
    RouteGroup,
    routeRulesUrl,
} from 'services/RouteRuleHelpers';

const firstRefusalColumns = [
    {
        header: 'Pharmacy Name',
        accessorKey: 'organization_name',
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'NPI',
        accessorKey: 'supplier_npi',
    },
];

const patientPreferenceColumns = ({ formik }) => [
    {
        header: 'Member ID',
        accessorKey: 'member_id',
        size: 150,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Therapeutic Category',
        accessorKey: 'therapeutic_category',
        size: 150,
        cell: ({ getValue }) => (
            <SecondaryData data={getValue() || NO_THERAPEUTIC_CATEGORY_DISPLAY_TEXT} />
        ),
    },
    {
        header: 'Pharmacy Name',
        accessorKey: 'organization_name',
        size: 150,
    },
    {
        header: 'NPI',
        accessorKey: 'current_supplier_npi',
        size: 100,
    },
    {
        header: 'Offer Rate',
        accessorKey: 'offer_rate',
        size: 120,
        cell: ({ getValue, row }) =>
            getValue() != null ? (
                <SecondaryData
                    data={
                        <ServicingRate
                            servicingRate={getValue()}
                            referentPriceType={row.original.referent_price_type}
                        />
                    }
                />
            ) : (
                <SecondaryData data="Market Rate" />
            ),
    },
    {
        header: 'End Date',
        accessorKey: 'end_date',
        size: 120,
        cell: ({ getValue }) => {
            const endDate = getValue();
            return (
                <span className="flex__gap--x-small">
                    <SecondaryData data={formatDate(endDate)} />
                    {endDate && dayjs().isAfter(endDate, 'day') && (
                        <Icon
                            icon={IconNames.WARNING_SIGN}
                            tooltipContent="End date is in the past"
                        />
                    )}
                </span>
            );
        },
    },
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.SECONDARY}
                    title="Delete"
                    onClick={() => formik.setFieldValue('relationshipToDelete', row.original)}
                />
            </div>
        ),
    },
];

const getPatientRelationshipRouteConfig = (relationshipType) => ({
    getAdditionalData: async ({ payerId, segmentId }) => {
        const patientRelationships = await get(
            patientRelationshipRoutesUrl(payerId, segmentId, relationshipType),
        );
        return { patientRelationships };
    },
    onSubmit: async ({ payerId, segmentId, values }) =>
        post(
            `${patientRelationshipRoutesUrl(payerId, segmentId, relationshipType)}/bulk_update`,
            values.patientRelationships,
        ),
    AdditionalContent: () => {
        const formik = useFormikContext();
        const [showAddPreferenceDialog, setShowAddPreferenceDialog] = useState(false);

        const suppliers = useGet('/reference/suppliers/parents');

        const gridData = formik.values.patientRelationships?.map((rel) => ({
            ...rel,
            organization_name: suppliers?.find(
                (supplier) => supplier.supplier_npi === rel.current_supplier_npi,
            )?.organization_name,
        }));

        return (
            <>
                <AddPatientRelationshipDialog
                    isOpen={showAddPreferenceDialog}
                    onClose={() => setShowAddPreferenceDialog(false)}
                    patientRelationshipType={relationshipType}
                    suppliers={suppliers}
                />
                <DeletePatientRelationshipDialog patientRelationshipType={relationshipType} />
                <ListPageHeader
                    pageTitle={`Configured ${PatientRelationshipTypeDisplayNameMapPluralized[relationshipType]}`}
                    buttons={[
                        {
                            label: `Add ${PatientRelationshipTypeDisplayNameMap[relationshipType]}`,
                            onClick: () => setShowAddPreferenceDialog(true),
                        },
                    ]}
                />
                <GridWithSearch
                    data={gridData}
                    isLoading={!gridData}
                    sortProperties={[
                        { id: 'member_id', desc: false },
                        { id: 'therapeutic_category', desc: false },
                    ]}
                    columns={patientPreferenceColumns({ formik })}
                />
            </>
        );
    },
});

/* eslint-disable react/prop-types */
const DialogContentForRoute = {
    [ContinuityOfCareRoute.CLAIMS_CHECK]: {
        getAdditionalData: async ({ payerId, segmentId }) => {
            const [mode, lookback] = await Promise.all([
                get(
                    `/clients/admin/${payerId}/${segmentId}/payer_option/${PayerOption.CLAIMS_CHK}`,
                ),
                get(
                    `/clients/admin/${payerId}/${segmentId}/payer_option/${PayerOption.CLAIMSLOOK}`,
                ),
            ]);
            return { mode: mode?.option_value, lookback_days: lookback?.option_value };
        },
        validationSchema: {
            mode: Yup.string().when('is_active', {
                is: true,
                then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
            }),
            lookback_days: Yup.number()
                .required(validationStrings.requiredField.errorMessage)
                .min(0, validationStrings.number.positiveNumbersOnly),
        },
        onSubmit: async ({ payerId, segmentId, values }) =>
            Promise.all([
                await put(`/clients/admin/${payerId}/${segmentId}/payer_option`, {
                    option_name: PayerOption.CLAIMS_CHK,
                    option_description: 'How should we check prior claims for routing?',
                    option_value: values.mode,
                }),
                await put(`/clients/admin/${payerId}/${segmentId}/payer_option`, {
                    option_name: PayerOption.CLAIMSLOOK,
                    option_description: 'How far back should we look at claims for routing?',
                    option_value: String(values.lookback_days),
                }),
            ]),
        AdditionalContent: () => {
            const formik = useFormikContext();
            return (
                <>
                    <RadioButtonGroupFormik
                        label="Mode"
                        name="mode"
                        required
                        options={[
                            { value: ClaimsCheckMode.ALL, text: 'Look at all prior claims' },
                            {
                                value: ClaimsCheckMode.DRUG,
                                text: 'Look at all prior claims for the same Drug',
                            },
                            {
                                value: ClaimsCheckMode.THERAPY,
                                text: 'Look at all prior claims for the same Therapeutic Category',
                            },
                        ]}
                        onChange={(newVal) =>
                            formik.setValues({
                                ...formik.values,
                                is_active: !!newVal,
                                mode: newVal,
                            })
                        }
                    />
                    <NumberInputFormik
                        label="Lookback Days For Claims"
                        id="lookback_days"
                        required
                        maxDecimalPlaces={0}
                        min={0}
                    />
                </>
            );
        },
    },
    [ContinuityOfCareRoute.MEMBER_PREFERENCE]: getPatientRelationshipRouteConfig(
        PatientRelationshipType.MEM_PREF,
    ),
    [ContinuityOfCareRoute.THERAPY_CONTINUITY]: getPatientRelationshipRouteConfig(
        PatientRelationshipType.THRPY_CONT,
    ),
    [ContinuityOfCareRoute.FIRST_REFUSAL_CLAIM]: {
        getAdditionalData: () => ({}),
        AdditionalContent: ({ payerId, segmentId }) => {
            const suppliers = useGet('/reference/suppliers/parents');
            const configuredNpis = useGet(
                payerId && segmentId
                    ? `/clients/admin/${payerId}/${segmentId}/first_refusal_claims/configured_suppliers`
                    : null,
            );

            const configuredSuppliers = useMemo(
                () =>
                    suppliers && configuredNpis
                        ? configuredNpis.map((npi) =>
                              suppliers.find((supplier) => supplier.supplier_npi === npi),
                          )
                        : undefined,
                [suppliers, configuredNpis],
            );

            return (
                <>
                    <ListPageHeader pageTitle="Configured Pharmacies" />
                    <GridWithSearch
                        data={configuredSuppliers}
                        isLoading={!configuredSuppliers}
                        sortProperties={[{ id: 'organization_name', desc: false }]}
                        columns={firstRefusalColumns}
                    />
                </>
            );
        },
    },
};
/* eslint-enable react/prop-types */

export const ContinuityOfCareRouteDetails = () => {
    const { segmentId, benefit, routeRuleId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(routeRulesUrl(segmentId, benefit));

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegment = payerSegments?.find((segment) => segment.segment_id === segmentId);
    const payerId = payerSegment?.payer_id;

    const { getAdditionalData, validationSchema, onSubmit, AdditionalContent } =
        DialogContentForRoute[routeRuleId];

    const [routes, setRoutes] = useState();
    const [additionalData, setAdditionalData] = useState();
    useEffect(() => {
        void (async () => {
            try {
                if (payerId && segmentId && benefit) {
                    setRoutes(await getRoutes(payerId, segmentId, benefit));
                    setAdditionalData(await getAdditionalData({ payerId, segmentId }));
                }
            } catch (err) {
                handleError('Error fetching routes', err);
            }
        })();
        return () => {
            setRoutes();
        };
    }, [payerId, segmentId, benefit, getAdditionalData]);

    const route = routes
        ?.filter(
            (group) =>
                ![RouteGroup.BEFORE_CONTINUITY, RouteGroup.AFTER_CONTINUITY].includes(
                    group.route_group,
                ),
        )
        ?.flatMap((group) => group.data)
        ?.find((r) => r.route_rule_id === routeRuleId);

    return (
        <Formik
            initialValues={{ ...route, ...additionalData }}
            enableReinitialize
            validationSchema={Yup.object({
                route_name: Yup.string().required(validationStrings.requiredField.errorMessage),
                route_description: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                is_active: Yup.bool().required(validationStrings.requiredField.errorMessage),
                ...validationSchema,
            })}
            onSubmit={async (values) => {
                try {
                    await onSubmit?.({ payerId, segmentId, values });

                    SuccessToast({ message: 'Route Rule saved successfully.' });

                    goHome();
                } catch (err) {
                    handleError('Error saving route rule.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={!(route && additionalData)}
                    formTitle="Edit Route Rule"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="Route Rule Display Name"
                                id="route_name"
                                required
                                disabled
                            />
                            <TextAreaFormik
                                label="Route Rule Description"
                                id="route_description"
                                required
                                disabled
                            />
                            <RadioButtonGroupFormik
                                label="Status"
                                name="is_active"
                                required
                                disabled={
                                    ![ContinuityOfCareRoute.CLAIMS_CHECK].includes(routeRuleId)
                                }
                                options={['Active', 'Inactive']}
                                selectedValue={
                                    formik.values.is_active === true ? 'Active' : 'Inactive'
                                }
                                onChange={(newVal) =>
                                    formik.setValues({
                                        ...formik.values,
                                        is_active: newVal === 'Active',
                                        mode: undefined,
                                    })
                                }
                            />
                            <AdditionalContent payerId={payerId} segmentId={segmentId} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
